import _ from 'lodash-es'
import { format as d3Format } from 'd3-format'

/**
 * Format value considering the `integerThreshold` and `digitsSI` options:
 * if `value` is in the range (-1000, 1000) then it returns the value rounded to the nearest integer
 * otherwise it formats `value` using the SI standard with `digitsSI` and replacing G with B
 * @param value value to format
 * @param options options
 */
export function formatSIWithIntegerThreshold(
  value: number,
  options?: { integerThreshold?: number; digitsSI?: number; digitsThreshold?: number }
): string {
  const defaultedOptions = Object.assign(
    {},
    { integerThreshold: 1000, digitsSI: 2, digitsThreshold: 0 },
    options
  )
  const { integerThreshold, digitsSI, digitsThreshold } = defaultedOptions
  if (value < integerThreshold && value > -integerThreshold) {
    return _.round(value, digitsThreshold).toLocaleString('us-US')
  }
  return formatSI(value, { digitsSI })
}

/**
 * Format value using SI format replacing G with B for Billion
 */
export function formatSI(value: number, { digitsSI = 2 } = {}) {
  const formatted = d3Format(`.${digitsSI}s`)(value)
  return formatted.endsWith('G') ? formatted.replace(/G/, 'B') : formatted
}

export function formatDollars(value: number, withDollarSymbol = true): string {
  if (withDollarSymbol) return `$ ${value.toLocaleString('us-US')}`
  return value.toLocaleString('us-US')
}

/**
 * formatPercentageUniversal should solve all problems on percentages formatting.
 * It shoukd be passed the percentage as a number in the [0, 100] interval.
 * It works with significant digits:
 *   + from 10% to 100% -> 0 decimals
 *   + from 1.0% to 9.9% -> 1 decimal
 *   + from 0.00% to 0.99% -> 2 decimals
 * The second parameter can further configure, adding more significant digits when needed.
 */
export function formatPercentageUniversal(p: number, { significant = 2 } = {}): string {
  return `${p.toLocaleString('us-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    maximumSignificantDigits: significant,
  })}%`
}

export function formatPercentageRound(p: number, { decimals = 0 } = {}): string {
  return `${p.toLocaleString('us-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`
}

export function formatNumberRound(p: number, { decimals = 0 } = {}): string {
  return p.toLocaleString('us-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}
